import { format, isValid, parseISO } from "date-fns";

export const HEADER_MAPPING: Record<string, string> = {
  "domain name": "domain",
  status: "status",
  bought: "bought",
  ns: "ns",
  "block direct url": "block_direct_url",
  "ai bot protection is disabled": "ai_bot_protection_disabled",
  translated: "translated",
  live: "live",
  index: "index",
  "domain purchase purpose": "purchase_purpose", 
  "treasury comment": "treasury_comment",
  "responsible for domain": "responsible_person",
  expired: "expired",
  "301 redirect: domain to which redirect set/will be set": "redirect_target",
  "redirects from domains": "redirect_sources",
  "domain price": "price",
  "domain registrar": "registrar",
  type: "site_type",
  "type 2": "domain_type",
  geo: "geo",
  cms: "cms",
  "official site": "official_site",
  "associated domains": "associated_domains",
  "associated domains date": "associated_domains_date",
  keyword: "keyword",
  "keyword (brand)": "brand_keyword",
  "brand type": "brand_type",
  lang: "lang",
  batch: "batch",
  "is hreflang group": "is_hreflang_group",
  hreflang_group: "hreflang_group",
  domains: "related_domains",
  comment: "comment",
  offer: "offer",
  "offer types": "offer_type",
  "keitaro source added": "keitaro_source_added",
  "keitaro campaign added": "keitaro_campaign_added",
  "keitaro campaign id": "keitaro_campaign_id",
  "campaign link (only 1-page sites)": "campaign_link",
  "account name": "account_name",
  "adspower id": "adspower_id",
  "gsc connected": "gsc_connected",
  "gsc api": "gsc_api",
  "google analytics": "google_analytics",
  "bing account": "bing_account",
  bing: "bing_connected",
  seogets: "seogets",
  "add to dynahosting": "add_to_dynahosting",
  "taxonomy data": "taxonomy_data",
  "add to domains": "add_to_domains",
  "add page - add content": "add_page_content",
  "add page - add blocks": "add_page_blocks",
  "wordpress setup": "wordpress_setup",
  "domain added date": "domain_added_date",
  "purchase date": "purchase_date",
  "date to ns": "date_added_ns",
  "date added to jetsik": "date_added_jetsik",
  "date added to wp": "date_added_wp",
  "date added html": "date_added_html",
  "activation date": "activation_date",
  "status change date": "status_change_date",
  link: "link",
  www: "www",
  team: "team",
  "domain registrar - team": "registrar",
  "domain price - team": "price",
};

const BOOL_KEYS = new Set([
  "bought",
  "ns",
  "ai_bot_protection_disabled",
  "translated",
  "live",
  "index",
  "is_hreflang_group",
  "keitaro_source_added",
  "keitaro_campaign_added",
  "gsc_connected",
  "google_analytics",
  "bing_connected",
  "seogets",
  "add_to_dynahosting",
  "taxonomy_data",
  "add_to_domains",
  "add_page_content",
  "add_page_blocks",
  "wordpress_setup",
]);

const DATE_KEYS = new Set([
  "expired",
  "purchase_date",
  "domain_added_date",
  "date_added_ns",
  "date_added_jetsik",
  "date_added_wp",
  "date_added_html",
  "activation_date",
  "status_change_date",
  "associated_domains_date",
]);

const NUMBER_KEYS = new Set([
  "price",
]);

export type CsvCleanedValue = boolean | string | number | null;

/**
 * Maps one CSV cell to its database value.
 *
 * Careful: an empty cell is NOT "leave this alone". It becomes `null` (or
 * `false` for boolean columns), and ImportPage upserts every key in the row,
 * so an empty cell overwrites whatever is stored. A spreadsheet exported with
 * a column present but unfilled wipes that column for every domain in the
 * file — this is how ~6600 domains lost their `team`.
 *
 * Behaviour is pinned in import-config.test.ts; change the tests deliberately.
 */
export const cleanCsvValue = (
  key: string,
  value: unknown,
): CsvCleanedValue => {
  if (BOOL_KEYS.has(key)) {
    if (!value || String(value).trim() === "") return false;

    const v = String(value).toLowerCase().trim();
    return (
      v === "true" || v === "yes" || v === "1" || v === "+" || v === "checked"
    );
  }

  if (!value || String(value).trim() === "") return null;

  if (DATE_KEYS.has(key)) {
    const strVal = String(value).trim();
    
    // The first group is always read as the day. Month-first input ("3/13/2026")
    // yields month 13, which Postgres rejects and the whole 500-row batch fails.
    // Worse, ambiguous dates (both parts <= 12) are swapped silently with no error.
    const match = strVal.match(/^(\d{1,2})[\.\/-](\d{1,2})[\.\/-](\d{2,4})/);
    
    if (match) {
      const day = match[1].padStart(2, "0");
      const month = match[2].padStart(2, "0");
      let year = match[3];

      if (year.length === 2) {
        year = `20${year}`;
      }

      return `${year}-${month}-${day}`;
    }

    const d = parseISO(strVal);
    if (isValid(d)) {
      return format(d, "yyyy-MM-dd");
    }

    return null; 
  }

  if (NUMBER_KEYS.has(key)) {
    const strVal = String(value).trim();
    const numberVal = parseFloat(strVal.replace(',', '.'));

    return isNaN(numberVal) ? null : numberVal;
  }

  return String(value).trim();
};
